// Note: Use dynamic imports for external libs to avoid type resolution issues at lint time
// AUTH CLEANUP FIX V2 - deviceId persistence

// Edge Runtime compatibility
export const runtime = "nodejs";

import type { TLoginSchema } from "@/app/auth/sign-in/_modules/schema/signin-schema";

import { environmentConfig } from "../utils/environment-config";

import AgencyTokenManager from "./agencyTokenManager";
import api from "./api";
import config from "./config";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import supabaseAuthService from "./supabaseAuthService";
import { handleApiCall } from "../utils/apiResponseHandler";

// API Response wrapper type
interface ApiResponse<T> {
  data: T;
  message?: string;
  statusCode?: number;
}

// Auth response types matching backend API
interface AuthResponse {
  user?: {
    id: string;
    email: string;
    name: string;
    phone?: string;
    accountType: 'trial' | 'lite' | 'basic' | 'full_fledged' | 'enterprise' | 'super_agent';
    userType: 'agent' | 'admin' | 'agency_owner' | 'viewer';
    isActive: boolean;
    emailVerified: boolean;
    phoneVerified: boolean;
    lastLoginAt?: string;
    isOnline?: boolean;
    mfaSettings?: {
      smsEnabled: boolean;
      totpEnabled: boolean;
      backupCodes: string[];
      trustedDevices: string[];
      preferredMethod: 'sms' | 'totp';
      sessionTimeoutMinutes: number;
      riskBasedAuth?: boolean;
    };
    trial?: {
      isTrialActive: boolean;
      trialStartDate: string;
      trialEndDate: string;
      trialExpiresAt: string;
    };
  };
  tokens?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
  accessToken?: string;
  refreshToken?: string;
  requires2FA?: boolean;
  userId?: string;
  message?: string;
  cooldownRemaining?: number;
  isMultiAgencyUser?: boolean;
  isIndependentAgent?: boolean;
  agencies?: {
    agency: Record<string, unknown>;
    role: string;
    permissions: string[];
  }[];
  expiresAt?: string;
  tokenType?: string;
  nextStep?: 'mfa' | 'agency_select' | 'dashboard' | 'pending_approval';
  session?: any;
}

const secretKey = environmentConfig.NEXT_PUBLIC_SECRET_KEY;
const key = new TextEncoder().encode(secretKey);
const isProd = environmentConfig.NODE_ENV === 'production';

export const SESSION_NAME = environmentConfig.NEXT_PUBLIC_SESSION_NAME;

// Local storage/session storage keys
const DEVICE_ID_STORAGE_KEY = "insuracentral_device_id";
const PENDING_2FA_STORAGE_KEY = "insuracentral_pending_2fa";

// CRITICAL DEBUG: Monitor ALL localStorage operations to catch deviceId removal
if (typeof window !== 'undefined') {
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);
  const originalClear = localStorage.clear.bind(localStorage);

  localStorage.removeItem = function(key: string) {
    if (key === DEVICE_ID_STORAGE_KEY) {
      console.error('[DEVICEID-REMOVAL-CAUGHT] ❌ localStorage.removeItem("insuracentral_device_id") was called!');
      console.error('[DEVICEID-REMOVAL-CAUGHT] Stack trace:', new Error().stack);
    }
    originalRemoveItem(key);
  };

  localStorage.clear = function() {
    console.error('[DEVICEID-REMOVAL-CAUGHT] ❌ localStorage.clear() was called - this will remove deviceId!');
    console.error('[DEVICEID-REMOVAL-CAUGHT] Stack trace:', new Error().stack);
    originalClear();
  };
}

export function getOrCreateDeviceId(): string {
  if (typeof window === "undefined") return "server";
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

    // MIGRATION: Check if existing deviceId is in old format (device-XXXXXXX)
    // If so, generate a new UUID to replace it
    if (existing) {
      const isOldFormat = /^device-\d+$/.test(existing) || existing === 'server' || existing === 'unknown' || existing.length < 10;
      if (isOldFormat) {
        // Fall through to generate new UUID
      } else {
        return existing;
      }
    }
    
    // Generate a UUID v4-like id with better cross-browser compatibility
    let newId: string;
    try {
      // Try crypto.randomUUID first (modern browsers)
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        newId = crypto.randomUUID();
      } else {
        // Fallback for older browsers - generate a proper UUID v4
        newId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
    } catch (cryptoError) {
      // Final fallback if crypto fails
      newId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    localStorage.setItem(DEVICE_ID_STORAGE_KEY, newId);
    return newId;
  } catch (error) {
    console.error('[DEVICE-TRUST-DEBUG] getOrCreateDeviceId() - error:', error);
    return "unknown";
  }
}

async function getClientIpAddress(timeoutMs = 1500): Promise<string | undefined> {
  if (typeof window === "undefined") return undefined;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const resp = await fetch("https://api.ipify.org?format=json", { signal: controller.signal });
    clearTimeout(timeout);
    if (!resp.ok) return undefined;
    const data = (await resp.json()) as { ip?: string };
    return data.ip;
  } catch {
    return undefined;
  }
}

async function getJose() {
  try {
    // Try to import jose with better error handling
    const joseModule = await import("jose");
    
    // Check if the module loaded correctly
    if (!joseModule || !joseModule.SignJWT) {
      throw new Error('Jose module did not load correctly');
    }
    
    const { SignJWT, jwtVerify } = joseModule;
    
    if (!SignJWT || !jwtVerify) {
      throw new Error('Required jose functions not available');
    }
    
    return { SignJWT, jwtVerify };
  } catch (error) {

    throw new Error('JWT operations not supported in current runtime');
  }
}

// Removed getCookies - using Supabase auth instead

// Removed legacy HS256 encrypt/decrypt functions
// Now using Supabase ES256 tokens exclusively

export async function login(
  formData: TLoginSchema,
  turnstileToken?: string
): Promise<AuthResponse> {
  try {
    const deviceId = getOrCreateDeviceId();

    // Use Supabase authentication with email
    const authResult = await supabaseAuthService.signInWithPassword(
      formData.username, // This is actually email in the form
      formData.password,
      deviceId, // Always send deviceId for remember device feature
      turnstileToken
    );

    if (authResult.error) {
      throw new Error(authResult.error);
    }

    if (!authResult.user || !authResult.session) {
      throw new Error('Authentication failed');
    }

    // Handle MFA requirement
    if (authResult.nextStep === 'mfa') {
      // Store MFA context for verification
      try {
        const pending = {
          userId: authResult.user.id,
          session: authResult.session,
          turnstileToken,
          deviceId, // Always include deviceId for remember device feature
        };

        if (typeof window !== "undefined") {
          sessionStorage.setItem(PENDING_2FA_STORAGE_KEY, JSON.stringify(pending));
        }
      } catch {
        // Silent fail - session storage may not be available
      }
      
      return {
        user: {
          ...authResult.user,
          name: authResult.user.name || authResult.user.email, // Fallback to email if name is not provided
        },
        requires2FA: true,
        nextStep: 'mfa',
      };
    }

    // Set user status to available immediately after login
    // This ensures call forwarding stops immediately when user logs in
    // Only run on client-side (typeof window !== 'undefined')
    if (authResult.session?.access_token && typeof window !== 'undefined') {
      try {
        const apiUrl = getApiUrl('users/status');
        await fetch(`${apiUrl}?token=${authResult.session.access_token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'available' }),
        }).catch((err) => {
          console.error('[Auth] Failed to set status after login:', err);
          // Ignore errors - status update is best-effort
        });
      } catch (err) {
        console.error('[Auth] Error setting status after login:', err);
        // Ignore errors - continue with login
      }
    }

    // Return the response data for further processing
    return {
      user: {
        ...authResult.user,
        name: authResult.user.name || authResult.user.email, // Fallback to email if name is not provided
      },
      session: authResult.session,
      nextStep: authResult.nextStep,
      agencies: authResult.agencies,
    };
  } catch (error) {

    throw error;
  }
}
export async function resend2FACode(userId: string) {
  try {
    const result = await handleApiCall<AuthResponse>(async () => {
      const res = await api.post<ApiResponse<AuthResponse>>({
        url: getApiUrl(endpoint.auth.postResend2fa),
        body: { userId },
        auth: false,
      });
      return res;
    });
    return result;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to resend code"
    );
  }
}

/**
 * Resend SMS code for action verification
 */
export async function resendSMSForAction(): Promise<void> {
  try {
    const session = await getSupabaseSession();
    const userId = session?.user?.id;

    if (!userId) {
      throw new Error('User session not found');
    }

    await api.post<ApiResponse<{ message: string }>>({
      url: getApiUrl('/auth/2fa/resend'),
      body: { userId },
      auth: true,
    });
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to send SMS code"
    );
  }
}

/**
 * Verify MFA code for sensitive actions (e.g., CSV export)
 * Does not create a session, just verifies the code is valid
 */
export async function verifyMFAForAction(code: string, method: 'totp' | 'sms' | 'backup' = 'totp') {
  try {
    // api.post returns the backend response directly (axios interceptor extracts response.data)
    // Backend returns: { statusCode, message, data: { verified: true }, timestamp }
    // So res = { statusCode, message, data: { verified: true }, timestamp }
    // And res.data = { verified: true }
    const res = await api.post({
      url: getApiUrl('/auth/mfa/verify-action'),
      body: {
        code,
        method,
      },
      auth: true,
    });

    // Access the nested data.verified - the response structure varies based on axios config
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = res as any;
    return response?.data?.verified || false;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to verify MFA code"
    );
  }
}

export async function verify2FACode(userId: string, code: string) {
  try {
    const deviceId = getOrCreateDeviceId();
    const ipAddress = await getClientIpAddress();

    // Try to retrieve session data saved during login (when requires2FA)
    let refreshToken: string | undefined = undefined;
    let pendingSession: { access_token?: string; refresh_token?: string } | undefined = undefined;
    try {
      if (typeof window !== "undefined") {
        const pendingRaw = sessionStorage.getItem(PENDING_2FA_STORAGE_KEY);
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw) as { refreshToken?: string; session?: { access_token?: string; refresh_token?: string } };
          refreshToken = pending?.refreshToken;
          pendingSession = pending?.session;
        }
      }
    } catch {
      // Silent fail - session storage may not be available
    }

    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postVerify2fa),
      body: {
        userId,
        code,
        deviceId,
        ...(ipAddress ? { ipAddress } : {}),
        ...(refreshToken ? { refreshToken } : {}),
      },
      auth: false,
      // MFA API GATE: the backend keys its "this session completed a second
      // factor" marker on the Supabase SESSION id, which it can only read from a
      // bearer token. `auth: false` stays — the axios interceptor can't attach a
      // token here because the session is still PENDING (held in sessionStorage,
      // not yet installed in the Supabase client), which is why this call was
      // unauthenticated in the first place. So the pending token is passed
      // explicitly. Without it the user verifies successfully and is then
      // refused by the gate on every subsequent request.
      ...(pendingSession?.access_token
        ? {
            options: {
              headers: { Authorization: `Bearer ${pendingSession.access_token}` },
            },
          }
        : {}),
    });

    const responseData = res.data.data;

    // Calculate expiration based on token expiration or default to 1 week
    const expirationTime = responseData?.expiresAt
      ? new Date(responseData.expiresAt).getTime()
      : Date.now() + 3600 * 1000 * 24 * 7;

    const expires = new Date(expirationTime);
    // Keep cookie small: id/email/userType plus accessToken (for client RBAC)
    const rawUser2FA = (responseData && typeof responseData === 'object' && 'user' in (responseData as object) && typeof (responseData as AuthResponse).user === 'object')
      ? ((responseData as AuthResponse).user as Record<string, unknown>)
      : undefined;
    const sessionUser = {
      user: {
        id: typeof rawUser2FA?.id === 'string' ? rawUser2FA.id : undefined,
        email: typeof rawUser2FA?.email === 'string' ? rawUser2FA.email : undefined,
        userType: rawUser2FA?.userType,
      },
      accessToken: responseData?.accessToken,
    };
    // Note: Using Supabase ES256 tokens - no need for custom session cookies
    // Session management is handled by Supabase client

    // Note: Using Supabase ES256 tokens - no need to store additional tokens
    // Supabase handles all token management automatically

    // Note: Using Supabase auth instead of cookies

    // NOTE: Do NOT clear pending 2FA context here!
    // handle2FASuccess needs the pending session tokens as a fallback
    // in case the Supabase client ends up with no session (multi-device scenario).
    // handle2FASuccess will clear it after successfully using the token.

    // Set Supabase session with the returned tokens or from pending session
    // This is critical for subsequent API calls that need authentication
    try {
      const { getSupabaseClient } = await import('./supabaseClient');
      const supabase = await getSupabaseClient();

      // Priority: 1. Response tokens (if Supabase RS256), 2. Pending session tokens (Supabase)
      // Use type assertion since backend may return additional token fields
      const responseAny = responseData as Record<string, unknown> | undefined;

      // Helper to check if token is Supabase (RS256) vs backend (HS256)
      const isSupabaseToken = (token: string | undefined): boolean => {
        if (!token) return false;
        try {
          const header = JSON.parse(atob(token.split('.')[0]));
          // Supabase uses RS256, backend uses HS256
          return header.alg === 'RS256' || header.alg === 'ES256';
        } catch {
          return false;
        }
      };

      // Prefer Supabase token for session - check if response tokens are Supabase format
      const responseAccessToken = (responseAny?.supabaseAccessToken as string) || (responseAny?.accessToken as string);
      const pendingAccessToken = pendingSession?.access_token;

      // Use Supabase token (from pending session) if response token is HS256 (backend token)
      const accessToken = isSupabaseToken(responseAccessToken) ? responseAccessToken : (pendingAccessToken || responseAccessToken);
      const refreshTokenForSession = (responseAny?.supabaseRefreshToken as string) || (responseAny?.refreshToken as string) || pendingSession?.refresh_token || '';

      if (accessToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshTokenForSession,
        });

        // Get a valid token for the status update call
        // Priority: 1. Supabase client session, 2. The token we just tried to set
        let validToken: string | null = null;

        if (!error) {
          const { data: sessionData } = await supabase.auth.getSession();
          validToken = sessionData?.session?.access_token || null;
        }

        // If setSession failed, the Supabase client may be empty.
        // Use the pending session token directly - it's still a valid Supabase JWT
        // that the backend will accept for the status update.
        if (!validToken && accessToken) {
          validToken = accessToken;
        }

        // Set user status to available after 2FA verification
        if (validToken) {
          try {
            const apiUrl = getApiUrl('users/status');
            await fetch(`${apiUrl}?token=${validToken}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'available' }),
            });
          } catch {
            // Silent fail - status update is best-effort
          }
        }
      }
    } catch {
      // Silent fail - session setup is best-effort
    }

    return responseData;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to verify code"
    );
  }
}

export async function logout() {
  try {
    // Set user status to offline before logging out
    // This ensures call forwarding works correctly when user logs out
    try {
      const { getSupabaseClient } = await import('./supabaseClient');
      const supabase = await getSupabaseClient();
      const { data: sessionData } = await supabase.auth.getSession();

      if (sessionData?.session?.access_token) {
        const apiUrl = getApiUrl('users/status');
        await fetch(`${apiUrl}?token=${sessionData.session.access_token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'offline' }),
        });
      }
    } catch (err) {
      console.error('[Auth] Error setting status to offline:', err);
      // Ignore errors - continue with logout
    }

    // Call backend logout endpoint
    await api.post({
      url: getApiUrl(endpoint.auth.postLogout),
      body: {},
      auth: true,
    });
  } catch (error) {
    // Continue with logout even if backend call fails

  }

  // Clear session heartbeat and active flag (for single-tab enforcement)
  // Also set logged_out flag to prevent new tabs from accessing after logout
  try {
    localStorage.removeItem('insuracentral_session_heartbeat');
    localStorage.removeItem('insuracentral_session_was_active');
    localStorage.setItem('insuracentral_logged_out', 'true');
    sessionStorage.removeItem('insuracentral_active_tab_id');
  } catch {
    // Silent fail
  }

  // Use comprehensive cleanup utility
  try {
    const { clearAllAuthData } = await import('../utils/auth-cleanup');
    clearAllAuthData();
  } catch (cleanupError) {

    // Fallback to basic cleanup
    try {
      // Clear localStorage tokens
      // NOTE: We do NOT clear DEVICE_ID_STORAGE_KEY - it must persist across logout
      // to enable "Remember Device" functionality for MFA
      localStorage.removeItem(config.tokenKey);
      // localStorage.removeItem(DEVICE_ID_STORAGE_KEY); // REMOVED - must persist across logout for device trust
      localStorage.removeItem('insuracentral_current_agency');
      localStorage.removeItem('insura_branding_cache');

      // Clear sessionStorage tokens
      sessionStorage.removeItem(PENDING_2FA_STORAGE_KEY);
      sessionStorage.removeItem('insuracentral_agency_token');
      sessionStorage.removeItem('insuracentral_agency_data');
      
      // Clear agency tokens
      AgencyTokenManager.clearAgencyToken();

      // Note: Using Supabase auth instead of cookies
    } catch (basicCleanupError) {

    }
  }
}

/**
 * True when the last getSupabaseSession() returned null because we could not
 * REACH the auth server, rather than because there is genuinely no session.
 *
 * getSession() normally reads localStorage and never touches the network — but
 * when the stored token is expired it refreshes internally, and that refresh can
 * fail on transport. Callers that would sign the user out must check this first.
 * See the matching flag on supabaseAuthService.refreshSession().
 */
let _lastGetSessionWasTransportFailure = false;
export function lastGetSessionWasTransportFailure(): boolean {
  return _lastGetSessionWasTransportFailure;
}

// Helper function to get Supabase session (replaces old getSession)
export async function getSupabaseSession() {
  _lastGetSessionWasTransportFailure = false;
  try {
    const { getSupabaseClient } = await import('./supabaseClient');
    const supabase = await getSupabaseClient();
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      // A 4xx is a real verdict (token rejected). Anything else — 5xx, no
      // status, a network stack error — means we never got an answer.
      const status = (error as { status?: number }).status ?? 0;
      _lastGetSessionWasTransportFailure = !(status >= 400 && status < 500);
      return null;
    }

    return session;
  } catch (error) {
    // Thrown here is always transport: failed fetch, DNS, abort, timeout.
    _lastGetSessionWasTransportFailure = true;
    return null;
  }
}

export async function googleLogin(idToken: string) {
  try {
    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postGoogleLogin),
      body: { idToken },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Google login failed"
    );
  }
}

export async function appleLogin(identityToken: string) {
  try {
    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postAppleLogin),
      body: { identityToken },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Apple login failed"
    );
  }
}

export async function microsoftLogin(accessToken: string) {
  try {
    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postMicrosoftLogin),
      body: { accessToken },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Microsoft login failed"
    );
  }
}

// Email and phone verification
export async function verifyEmail(token: string) {
  try {
    const res = await api.post<ApiResponse<{ emailVerified: boolean; verifiedAt: string }>>({
      url: getApiUrl(endpoint.auth.postVerifyEmail),
      body: { token },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Email verification failed"
    );
  }
}

export async function verifyPhone(phone: string, code: string) {
  try {
    const res = await api.post<ApiResponse<{ phoneVerified: boolean; verifiedAt: string }>>({
      url: getApiUrl(endpoint.auth.postVerifyPhone),
      body: { phone, code },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Phone verification failed"
    );
  }
}

// Password management
export async function changePassword(currentPassword: string, newPassword: string) {
  try {
    const res = await api.post<ApiResponse<{ passwordChanged: boolean; changedAt: string }>>({
      url: getApiUrl(endpoint.auth.postChangePassword),
      body: { currentPassword, newPassword },
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Password change failed"
    );
  }
}

// MFA management
export async function enableMfa(method: 'sms' | 'totp', phone?: string) {
  try {
    const res = await api.post<ApiResponse<{
      mfaEnabled: boolean;
      method: string;
      backupCodes: string[];
      qrCode?: string;
    }>>({
      url: getApiUrl(endpoint.auth.postEnableMfa),
      body: { method, ...(phone && { phone }) },
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "MFA enable failed"
    );
  }
}

export async function disableMfa() {
  try {
    const res = await api.post<ApiResponse<{ mfaDisabled: boolean }>>({
      url: getApiUrl(endpoint.auth.postDisableMfa),
      body: {},
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "MFA disable failed"
    );
  }
}

// Profile management
export async function getProfile() {
  try {
    const res = await api.get<ApiResponse<{ user: AuthResponse['user'] }>>({
      url: getApiUrl(endpoint.auth.getProfile),
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to get profile"
    );
  }
}

export async function updateProfile(profileData: {
  name?: string;
  phone?: string;
}) {
  try {
    const res = await api.patch<ApiResponse<{ user: AuthResponse['user'] }>>({
      url: getApiUrl(endpoint.auth.patchProfile),
      body: profileData,
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Profile update failed"
    );
  }
}

// Logout all devices
export async function logoutAll() {
  try {
    const res = await api.post<ApiResponse<{ allDevicesLoggedOut: boolean; loggedOutAt: string }>>({
      url: getApiUrl(endpoint.auth.postLogoutAll),
      body: {},
      auth: true,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Logout all failed"
    );
  }
}

export async function register(formData: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  accountType?: 'trial' | 'basic' | 'premium' | 'enterprise';
  userType?: 'agent' | 'admin' | 'agency_owner' | 'viewer';
  turnstileToken?: string;
}) {
  try {
    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postRegister),
      body: { 
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`,
      },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "An error occurred during registration"
    );
  }
}

export async function requestPasswordReset(email: string) {
  try {
    const res = await api.post<ApiResponse<{ message: string }>>({
      url: getApiUrl(endpoint.auth.postRequestPasswordReset),
      body: { email },
      auth: false,
    });

    // Backend returns { statusCode, message, data: null, timestamp }
    // Check for successful status code (axios already handles HTTP errors)
    const responseData = res.data || res;
    if ((responseData as any).statusCode === 200) {
      return { message: (responseData as any).message };
    }

    // If we got here without throwing, consider it a success
    return responseData;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to request password reset"
    );
  }
}

export async function resetPassword(token: string, newPassword: string) {
  try {
    const res = await api.post<ApiResponse<{ message: string }>>({
      url: getApiUrl(endpoint.auth.postResetPassword),
      body: { token, newPassword },
      auth: false,
    });

    return res.data.data;
  } catch (error: unknown) {
    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to reset password"
    );
  }
}

export async function refreshToken() {
  try {
    // Get stored refresh token
    const storedTokenData = localStorage.getItem(config.tokenKey);
    if (!storedTokenData) {
      throw new Error('No refresh token found');
    }

    const tokenData = JSON.parse(storedTokenData);
    if (!tokenData.refreshToken) {
      throw new Error('No refresh token available');
    }

    const res = await api.post<ApiResponse<AuthResponse>>({
      url: getApiUrl(endpoint.auth.postRefreshToken),
      body: { refreshToken: tokenData.refreshToken },
      auth: false, // Don't use expired access token
    });

    const responseData = res.data.data;

    if (responseData?.accessToken) {
      // Calculate new expiration
      const expirationTime = responseData?.expiresAt
        ? new Date(responseData.expiresAt).getTime()
        : Date.now() + 3600 * 1000 * 24 * 7;

      const expires = new Date(expirationTime);

      // Update the session with new token (minimal payload)
      const rawUserRefresh = (responseData && typeof responseData === 'object' && 'user' in (responseData as object) && typeof (responseData as AuthResponse).user === 'object')
        ? ((responseData as AuthResponse).user as Record<string, unknown>)
        : undefined;
      const sessionUser = {
        user: {
          id: typeof rawUserRefresh?.id === 'string' ? rawUserRefresh.id : undefined,
          email: typeof rawUserRefresh?.email === 'string' ? rawUserRefresh.email : undefined,
          userType: rawUserRefresh?.userType,
        },
        accessToken: responseData?.accessToken,
      };
      // Note: Using Supabase ES256 tokens - no need for custom session cookies
      // Session management is handled by Supabase client

      // Note: Using Supabase ES256 tokens - no need to store additional tokens
      // Supabase handles all token management automatically

      // Note: Using Supabase auth instead of cookies
    }

    return responseData;
  } catch (error: unknown) {
    // Clear invalid tokens
    localStorage.removeItem(config.tokenKey);
    // Note: Using Supabase auth instead of cookies

    const errorResponse = error as {
      response?: { data?: { message?: string } };
    };
    throw new Error(
      errorResponse?.response?.data?.message || "Failed to refresh token"
    );
  }
}

// Utility function to check if token is expired
export function isTokenExpired(expiresAt?: string): boolean {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() <= Date.now();
}

// Debug function to check token availability
export function debugTokenStatus(): {
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  expiresAt: string | undefined;
  isExpired: string;
  tokenSource: string;
  tokenPreview: string | null;
} {
  try {
    // Check localStorage tokens
    const tokenData = localStorage.getItem(config.tokenKey);
    
    if (tokenData) {
      const parsed = JSON.parse(tokenData);
      const hasAccess = !!parsed.accessToken;
      const hasRefresh = !!parsed.refreshToken;
      const expiresAt = parsed.expiresAt;
      const expired = expiresAt ? isTokenExpired(expiresAt) : 'unknown';
      
      return {
        hasAccessToken: hasAccess,
        hasRefreshToken: hasRefresh,
        expiresAt,
        isExpired: expired ? 'yes' : 'no',
        tokenSource: 'localStorage',
        tokenPreview: hasAccess ? parsed.accessToken.substring(0, 50) + '...' : null
      };
    }
    
    return {
      hasAccessToken: false,
      hasRefreshToken: false,
      expiresAt: undefined,
      isExpired: 'unknown',
      tokenSource: 'none',
      tokenPreview: null
    };
  } catch (error) {

    return {
      hasAccessToken: false,
      hasRefreshToken: false,
      expiresAt: undefined,
      isExpired: 'error',
      tokenSource: 'error',
      tokenPreview: null
    };
  }
}

// Enhanced session check function
export async function getEnhancedSession(): Promise<any | null> {
  try {
    // First check Supabase session
    const supabaseSession = await getSupabaseSession();
    
    if (supabaseSession) {
      // Build session object from Supabase data
      return {
        user: {
          id: supabaseSession.user.id,
          email: supabaseSession.user.email || '',
          name: supabaseSession.user.user_metadata?.name || supabaseSession.user.email || '',
          accountType: 'trial', // Default, should be updated from backend
          userType: 'agent', // Default, should be updated from backend
        },
        expires: new Date(supabaseSession.expires_at! * 1000).toISOString(),
      };
    }
    
    // Fallback to stored session data
    const tokenStatus = debugTokenStatus();

    if (tokenStatus.hasAccessToken) {
      // Try to decode the token to get user info
      try {
        const tokenData = localStorage.getItem(config.tokenKey);
        if (tokenData) {
          const parsed = JSON.parse(tokenData);
          const tokenPayload = JSON.parse(atob(parsed.accessToken.split('.')[1]));
          
          return {
            user: {
              id: tokenPayload.sub || tokenPayload.userId || '',
              email: tokenPayload.email || '',
              name: tokenPayload.name || tokenPayload.email || '',
              accountType: tokenPayload.accountType || 'trial',
              userType: tokenPayload.userType || 'agent',
            },
            expires: parsed.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          };
        }
      } catch (decodeError) {

      }
    }
    
    return null;
  } catch (error) {

    return null;
  }
}

// Utility function to get token expiration time
export function getTokenExpiration(token?: string): Date | null {
  if (!token) return null;

  try {
    // Decode JWT without verification to get expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      return new Date(payload.exp * 1000);
    }
  } catch (error) {

  }

  return null;
}

// Enhanced session validation
export async function validateSession(): Promise<boolean> {
  try {
    // Note: getSession returns null for httpOnly cookies
    // For now, we'll rely on the backend to verify the session
    return true; // Assume session is valid if we reach this point
  } catch (error) {

    return false;
  }
}

export async function updateSession(request: unknown) {
  try {
    const req = (request && typeof request === 'object' && 'cookies' in (request as object))
      ? (request as { cookies: { get: (name: string) => { value?: string } | undefined } })
      : undefined;

    if (!req) {
      return;
    }

    const session = req?.cookies?.get(config.SESSION_NAME || "")?.value;
    
    if (!session) {
      return;
    }

    const { NextResponse } = await import("next/server");
    
    // Note: Using Supabase ES256 tokens - session management is handled by Supabase
    // No need for custom cookie-based session management
    return NextResponse.next();
  } catch (error) {

    return;
  }
}
